<script lang="ts" setup>
defineProps<{
  /** Icon name (mdi-* format) */
  icon: string
  /** Title text */
  title: string
  /** Description text */
  description?: string
  /** Action button text */
  actionText?: string
  /** Action button icon */
  actionIcon?: string
}>()

defineEmits<{
  (e: 'action'): void
}>()
</script>

<template>
  <section class="dashboard-empty-state">
    <div class="dashboard-empty-icon">
      <v-icon :icon="icon" size="64" />
    </div>
    <h3 class="dashboard-empty-title">{{ title }}</h3>
    <p v-if="description" class="dashboard-empty-text">{{ description }}</p>
    <button
      v-if="actionText"
      type="button"
      class="dashboard-empty-action"
      @click="$emit('action')"
    >
      <v-icon v-if="actionIcon" :icon="actionIcon" size="18" />
      {{ actionText }}
    </button>
    <!-- Custom action slot -->
    <slot name="action" />
  </section>
</template>

<style lang="scss" scoped>
/* Component uses global dashboard-* classes from dashboard-common.css */
/* Only component-specific adjustments here */

.dashboard-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;
}

.dashboard-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(15, 23, 48, 0.6);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;
  color: rgba(200, 218, 255, 0.4);
  margin-bottom: var(--dashboard-section-margin);
}

.dashboard-empty-title {
  font-size: var(--dashboard-stat-value-size);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 var(--dashboard-gap-sm);
}

.dashboard-empty-text {
  font-size: var(--dashboard-subtitle-size);
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 var(--dashboard-section-margin);
  max-width: 400px;
  line-height: 1.5;
}

.dashboard-empty-action {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: var(--dashboard-btn-padding);
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.25);
  border-radius: var(--dashboard-btn-radius);
  color: #81c784;
  font-size: var(--dashboard-subtitle-size);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.4);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .dashboard-empty-icon {
    background: rgba(240, 245, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.3);
  }

  .dashboard-empty-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .dashboard-empty-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .dashboard-empty-action {
    background: rgba(76, 175, 80, 0.1);
    border-color: rgba(76, 175, 80, 0.2);
    color: #4caf50;

    &:hover {
      background: rgba(76, 175, 80, 0.15);
      border-color: rgba(76, 175, 80, 0.3);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-empty-action {
    transition: none;
  }
}
</style>
